import { Controller } from "@hotwired/stimulus"

// Cotejo factura vs OC en la pantalla de confirmación del upload.
//
// 1) Semáforo inline: al elegir una PO en el dropdown "Vincular a PO existente",
//    pide el cotejo al server y muestra línea por línea qué coincide y qué difiere.
// 2) Modal de diferencias: al confirmar la importación con una PO vinculada que
//    difiere, intercepta el submit y muestra TODAS las diferencias. El operador
//    puede "Aceptar la factura como está" (la OC se ajusta a lo facturado,
//    accept_as_invoiced=1) o cerrar y conversar los cambios con el proveedor.
//
// El veredicto definitivo SIEMPRE lo recalcula el server al confirmar
// (defensa server-side en el update del controller).
export default class extends Controller {
  static targets = ["select", "result", "modal", "modalBody", "acceptFlag", "acceptButton"]
  static values = { url: String }

  connect() {
    if (this.hasSelectTarget && this.selectTarget.value) this.compare()
  }

  async compare() {
    const poId = this.selectTarget.value
    if (!poId) {
      this.resultTarget.innerHTML = ""
      return
    }

    this.resultTarget.innerHTML = '<div class="text-muted small py-2">Cotejando contra la OC…</div>'

    const data = await this.fetchComparison(poId)
    if (!data) return
    if (data.error) {
      this.resultTarget.innerHTML = `<div class="alert alert-warning py-1 px-2 small mb-0">${data.error}</div>`
      return
    }
    this.render(data)
  }

  // Intercepta el submit de "Confirmar importación": si hay PO vinculada y el
  // cotejo difiere, muestra el modal en vez de submitear.
  async confirmSubmit(event) {
    if (this.skipCheck) return
    if (!this.hasSelectTarget || !this.selectTarget.value) return
    if (this.hasAcceptFlagTarget && this.acceptFlagTarget.value === "1") return

    event.preventDefault()
    this.submitter = event.submitter || null

    const data = await this.fetchComparison(this.selectTarget.value)
    if (!data || data.error) {
      // Sin cotejo no bloqueamos acá: la defensa server-side decide.
      this.submitForm()
      return
    }

    if (data.status === "approved") {
      this.submitForm()
      return
    }

    if (data.status === "rejected") {
      // No hay nada que aceptar: mostramos el motivo y NO submiteamos.
      // (El server también rechaza, por si el JS falla.)
      this.resultTarget.innerHTML = this.rejectedHtml(data)
      this.resultTarget.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    this.showModal(data)
  }

  // Con cotejo rechazado, "Aceptar y registrar" no tiene sentido: se apaga.
  toggleAcceptButton(status) {
    if (!this.hasAcceptButtonTarget) return
    const rejected = status === "rejected"
    this.acceptButtonTarget.disabled = rejected
    this.acceptButtonTarget.title = rejected ? "El cotejo rechazó esta factura — ver el motivo en rojo" : ""
  }

  rejectedHtml(data) {
    return `
      <div class="alert alert-danger py-2 px-3 mb-0">
        <strong>✕ Factura rechazada:</strong> ${data.reject_reason || "no corresponde a esta OC."}
        <div class="small mt-1">Verificá que el PDF sea el correcto o vinculá la OC que corresponde.
        No se puede aceptar con ajustes.</div>
      </div>`
  }

  acceptAndSubmit() {
    if (this.hasAcceptFlagTarget) this.acceptFlagTarget.value = "1"
    this.hideModal()
    this.submitForm()
  }

  submitForm() {
    this.skipCheck = true
    const form = this.element.closest("form") || this.element
    if (this.submitter) {
      form.requestSubmit(this.submitter)
    } else {
      form.requestSubmit()
    }
  }

  showModal(data) {
    if (!this.hasModalTarget) return

    this.modalBodyTarget.innerHTML = this.diffHtml(data)
    // Bootstrap 4 con jQuery: $ viene del ProvidePlugin de webpack (no de window)
    if (typeof $ !== "undefined" && $.fn.modal) {
      this.modalTarget.removeAttribute("aria-hidden")
      $(this.modalTarget).modal("show")
    } else {
      // Sin jQuery no podemos mostrar el modal: no bloqueamos al operador,
      // el server re-renderiza el edit con el alert de diferencias.
      this.submitForm()
    }
  }

  hideModal() {
    if (!this.hasModalTarget) return
    if (typeof $ !== "undefined" && $.fn.modal) $(this.modalTarget).modal("hide")
  }

  async fetchComparison(poId) {
    try {
      const response = await fetch(`${this.urlValue}?purchase_order_id=${poId}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      if (!response.ok) return { error: data.error || "No se pudo cotejar" }
      return data
    } catch (e) {
      return { error: "Error al cotejar contra la OC" }
    }
  }

  render(data) {
    const money = this.money
    const t = data.totals || {}

    this.toggleAcceptButton(data.status)

    if (data.status === "rejected") {
      // Rechazo directo: solo el motivo principal, sin tabla de diferencias.
      this.resultTarget.innerHTML = this.rejectedHtml(data)
      return
    }

    if (data.status === "approved") {
      // Sin brechas: un solo mensaje, sin tabla.
      const count = (data.lines || []).length
      this.resultTarget.innerHTML = `
        <div class="alert alert-success py-2 px-3 mb-0">
          <strong>✓ Todo coincide con tu pedido.</strong>
          ${count} ítem${count === 1 ? "" : "s"} verificado${count === 1 ? "" : "s"} contra la OC.
          Podés aceptarla directamente.
        </div>`
      return
    }

    // Con brechas: resumen en una línea + solo las filas con problema.
    const issues = this.issueCount(data)
    const banner = `
      <div class="alert alert-warning py-2 px-3 mb-2">
        <strong>⚠ ${issues} diferencia${issues === 1 ? "" : "s"} contra la OC.</strong>
        ${this.deltaSummary(t)}
        Al confirmar vas a ver el detalle para aceptar los ajustes.
      </div>`
    this.resultTarget.innerHTML = `${banner}${this.diffTable(data)}`
  }

  diffHtml(data) {
    const issues = this.issueCount(data)
    const t = data.totals || {}
    const intro = `
      <div class="alert alert-warning py-2 px-3 mb-2">
        <strong>⚠ ${issues} diferencia${issues === 1 ? "" : "s"} contra la OC ${data.po_description || "#" + data.po_id}.</strong>
        ${this.deltaSummary(t)}
      </div>
      <p class="small mb-3">Si aceptás la factura como está, la OC se ajusta a lo facturado y
      <strong>el pedido original queda guardado</strong> en el historial de ajustes.
      Si algo no corresponde, cerrá este aviso y conversá los cambios con el proveedor.</p>`
    return `${intro}${this.diffTable(data)}`
  }

  // Cantidad de brechas: líneas con desvío + faltantes + extras + total desviado.
  // Un par faltante↔extra que parece el mismo producto (cambio de presentación)
  // cuenta como UNA diferencia, no dos.
  issueCount(data) {
    const badLines = (data.lines || []).filter((l) => !(l.qty_ok && l.price_ok)).length
    const { pairs, restMissing, restExtra } = this.pairSubstitutions(data.missing || [], data.extra || [])
    const t = data.totals || {}
    return badLines + pairs.length + restMissing.length + restExtra.length + (t.ok ? 0 : 1)
  }

  // Empareja un ítem pedido-no-facturado con uno facturado-no-pedido cuando
  // comparten palabras ("kilo espinaca" ↔ "ATADO ESPINACA"): casi siempre es el
  // mismo producto en otra presentación, y mostrado como par se entiende solo.
  pairSubstitutions(missing, extra) {
    const tokens = (s) => new Set(
      String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter((w) => w.length > 2)
    )
    const overlap = (a, b) => {
      const ta = tokens(a), tb = tokens(b)
      if (!ta.size || !tb.size) return 0
      const inter = [...ta].filter((w) => tb.has(w)).length
      return inter / Math.min(ta.size, tb.size)
    }

    const pairs = []
    const usedExtra = new Set()
    const restMissing = []
    missing.forEach((m) => {
      let best = null, bestScore = 0
      extra.forEach((x, i) => {
        if (usedExtra.has(i)) return
        const s = overlap(m.sku_name, x.description_raw)
        if (s > bestScore) { best = i; bestScore = s }
      })
      if (best !== null && bestScore >= 0.5) {
        pairs.push({ m, x: extra[best] })
        usedExtra.add(best)
      } else {
        restMissing.push(m)
      }
    })
    return { pairs, restMissing, restExtra: extra.filter((_, i) => !usedExtra.has(i)) }
  }

  // "La factura es $X más cara / más barata que lo pedido."
  deltaSummary(t) {
    const delta = (t.invoiced_net_cents || 0) - (t.ordered_net_cents || 0)
    if (delta === 0) return ""
    const dir = delta > 0 ? "más cara" : "más barata"
    return `La factura es <strong>${this.money(Math.abs(delta))} ${dir}</strong> que lo pedido (neto).`
  }

  // Tabla SOLO con las filas que tienen problema; las que coinciden se resumen
  // en una línea al pie para no tapar las brechas.
  diffTable(data) {
    const money = this.money
    const qty = (q) => Number(q).toLocaleString("es-AR", { maximumFractionDigits: 3 })

    const okCount = (data.lines || []).filter((l) => l.qty_ok && l.price_ok).length
    const rows = (data.lines || []).filter((l) => !(l.qty_ok && l.price_ok)).map((l) => {
      const issues = []
      if (!l.qty_ok) issues.push(`cantidad: pedimos ${qty(l.ordered_qty)}, facturaron ${qty(l.invoiced_qty)}`)
      if (!l.price_ok) issues.push(`precio: ${money(l.ordered_price_cents)} → ${money(l.invoiced_price_cents)}`)
      const delta = l.delta_cents === 0 ? "" :
        ` <span class="${l.delta_cents > 0 ? "text-danger" : "text-success"}">(${l.delta_cents > 0 ? "+" : "−"}${money(Math.abs(l.delta_cents))})</span>`
      return `<tr class="table-warning">
        <td class="small font-weight-bold">${l.sku_name || l.description_raw}</td>
        <td class="small">${issues.join(" · ")}${delta}</td>
      </tr>`
    })

    const { pairs, restMissing, restExtra } = this.pairSubstitutions(data.missing || [], data.extra || [])

    const substitutions = pairs.map(({ m, x }) => {
      const sameQty = Number(m.quantity) === Number(x.quantity)
      const samePrice = Math.abs(m.net_price_cents - x.net_price_cents) <= 100
      const detail = sameQty && samePrice
        ? "misma cantidad y precio — parece el mismo producto con otra presentación"
        : `pedido ${qty(m.quantity)} × ${money(m.net_price_cents)} → facturado ${qty(x.quantity)} × ${money(x.net_price_cents)}`
      return `<tr class="table-warning"><td class="small font-weight-bold">${x.description_raw}</td>
        <td class="small">reemplaza a «${m.sku_name}» del pedido (${detail})</td></tr>`
    })

    const missing = restMissing.map((m) =>
      `<tr class="table-warning"><td class="small font-weight-bold">${m.sku_name}</td>
       <td class="small">pedido (${qty(m.quantity)} × ${money(m.net_price_cents)}) pero <strong>no vino en la factura</strong> — se quita de la OC</td></tr>`
    )
    const extra = restExtra.map((x) =>
      `<tr class="table-warning"><td class="small font-weight-bold">${x.description_raw}</td>
       <td class="small">facturado (${qty(x.quantity)} × ${money(x.net_price_cents)}) pero <strong>no estaba en la OC</strong> — se agrega</td></tr>`
    )

    const t = data.totals || {}
    const totalsRow = t.ok ? "" : `<tr class="table-warning font-weight-bold">
      <td class="small">Total neto</td>
      <td class="small">OC ${money(t.ordered_net_cents)} vs factura ${money(t.invoiced_net_cents)}</td></tr>`

    const okFooter = okCount > 0
      ? `<div class="small text-muted mt-1">✓ ${okCount} ítem${okCount === 1 ? "" : "s"} restante${okCount === 1 ? "" : "s"} coincide${okCount === 1 ? "" : "n"} con la OC.</div>`
      : ""

    return `<table class="table table-sm mb-0"><tbody>${rows.join("")}${substitutions.join("")}${missing.join("")}${extra.join("")}${totalsRow}</tbody></table>${okFooter}`
  }

  get money() {
    return (cents) => "$" + (cents / 100).toLocaleString("es-AR", { maximumFractionDigits: 2 })
  }
}
